
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from 'react-router-dom';

import { Box, Tab, Select, MenuItem } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import { styled } from "@mui/material/styles";
import { TabContext, TabList, TabPanel } from '@mui/lab';

import Chart from "react-apexcharts";
import dayjs from 'dayjs';

import VulnerabilityTrendAnalysis  from "./VulnerabilityTrendAnalysis";
import { fetchAlertAnalysis as fetchAlertAnalysisService} from "services/modules/rhymscan/data";
import { fetchReportDates as fetchReportDatesService } from "services/modules/rhymscan/report";
import { fetchSingleCompany as fetchSingleCompanyService} from "services/modules/idp/company";
import { setLatestReportDate as setLatestReportDateSlice} from  'stores/modules/rhymscan/reportreducer';
import { setLoading } from 'stores/modules/idp/authreducer';

// Sets style for te highlighted day ie scanned date on calendar
const HighlightedDay = styled(PickersDay)(({ theme }) => ({
  "&.Mui-selected": {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
}));

// Handles highlighting the scan date in calendar
const ServerDay = (props) => {
  const { highlightedDays = [], day, outsideCurrentMonth, ...other } = props;

  const isSelected =
    !props.outsideCurrentMonth &&
    highlightedDays.includes(day.format("YYYY-MM-DD"));

  return (
    <HighlightedDay
      {...other}
      outsideCurrentMonth={outsideCurrentMonth}
      day={day}
      selected={isSelected}
    />
  );
};

const CurrentAnalysis = () => {
 // useParams hook
 const { category } = useParams();

 // Redux dispatch
 const dispatch = useDispatch();

 // React router
 const navigate = useNavigate();
 
 // Redux selectors
 const dimensionsContainer = useSelector(state => state.rsData.dimensions);
 const selectedCompanyContainer = useSelector((state) => state.auth.companyData );
 const reportDateContainer = useSelector((state) => state.rsReport.latestReportDate);
 const severitiesContainer = useSelector(state => state.rsData.severities);
 const companyContainer = useSelector(state => state.company.singleCompany);

  // State variables
  let allFiltersData = [
    {
      label: "Last 1 Month",
      value: 1,
    },
    {
      label: "Last 3 Months",
      value: 3,
    },
    {
      label: "Last 6 Months",
      value: 6,
    },
    {
      label: "Last 9 months",
      value: 9,
    },
    {
      label: "Last 12 months",
      value: 12,
    },
  ];

  const [value, setValue] = React.useState('1');
  const [alertAnalysis, setAlertsAnalysis] = useState([]);
  const [filters, setfiltersData] = useState(allFiltersData);
  const [date, setActiveDate] = useState(null);
  const [companyCreatedDate, setCompanyCreatedDate] = useState(null);
  const [activeFilter, setActiveFilter] = useState(1);
  const [highlightedDays, setHighlitedDays] = useState([]);
  const [reportDates,setReportDates]=useState([]);

  // Current Analysis graph 
  let chartstate = {
    options: {
      colors: category === "compliance" ? ["#2fc430", "#eb4b2c"] : ["#2fc430", "#4baeec", "#4c4ccc", "#fc659c", "#eb4b2c"], 
      chart: {
        fontFamily: 'Montserrat, Open Sans, sans-serif',
        type: "bar",
        height: 275,
        toolbar: {
          show: false
        }
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "55%",
          endingShape: "rounded",
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        show: true,
        width: 2,
        colors: ["transparent"],
      },
      xaxis: {
        labels: {
          rotate: -45,
          hideOverlappingLabels: false,
          style: {
            fontSize: '10px',
            fontFamily: 'Nunito Sans, sans-serif',
            fontWeight: 0,
        },
        },
      },
      yaxis: {
        title: {
          text: "Findings Count",
        },
        min:0,
        max:20,
      },
      fill: {
        opacity: 1,
      },
      tooltip: {
        y: {
          formatter: function (val) {
            if(val > 20){
              return 20 + "+ Finding(s)";
            }
            else{
              return val + " Finding(s)";
            }
          },
        },
      },
      legend: {
        position: "top",
      },
    },
    series: [
      {
        name: "Informational",
        data: [],
      },
      {
        name: "Low",
        data: [],
      },
      {
        name: "Medium",
        data: [],
      },
      {
        name: "High",
        data: [],
      },
      {
        name: "Critical",
        data: [],
      },
    ],
  };

  const [currentAnalysisChatOptions, setCurrentAnalysisChatOptions] = useState(chartstate);
  
   // Handles classification of alerts based on severity and dimension in case if category is not compliance and sets to chartstate
  useEffect(() => {
    if (alertAnalysis && alertAnalysis.length > 0){
      if (category !== "compliance"){
        const series = severitiesContainer.map((severity) => ({
                        name: severity.label,
                        data: [],
                      }));
        
        alertAnalysis.forEach((dat) => {
          severitiesContainer.forEach((severity) => {
            const severityIndex = series.findIndex(
              (severe) => severe.name === severity.label
            );

            series[severityIndex].data.push(dat.counts[severity.slug]===0?0:dat.counts[severity.slug]);
          });
        });

        let categories = alertAnalysis.map((dat) =>
            dat.dimension.label.split(" ")
          );
        categories = categories.map((cat) => {
          if (cat[1] === "Reconnaissance") {
            return [cat[0], "Reconn-", "aissance"];
          }
          return cat;
        });
        chartstate.options = {
          ...chartstate.options,
          xaxis: { ...chartstate.options.xaxis, categories },
        };
        chartstate.series = series;
        chartstate.options.chart['events']= {
          dataPointSelection: (event, chartContext, config) => {
            let alert_types = [];
            if (category !== "compliance") {
              alert_types = alertAnalysis.map((dat) => dat.dimension.slug);
            } else {
              alert_types = alertAnalysis.map((dat) => dat.standard.slug);
            }
            const url = `/rs/category-threats-dashboard/category-threats?category=${category}&threat_type=${alert_types[config.dataPointIndex]}&alert_severity=${series[config.seriesIndex].name}&domain=${selectedCompanyContainer.domain}&date=${reportDateContainer}`;
            navigate(url);
          }
        }
        
        setCurrentAnalysisChatOptions(chartstate);
      }
      // Handles classification of alerts based on standard and dimension in case if category is compliance and sets to chart state
      else{
        const series = [
          {
            name: "Compliant",
            data: [],
          },
          {
            name: "Non-Compliant",
            data: [],
          },
        ];
        const compliantIndex = series.findIndex(
          (ser) => ser.name === "Compliant"
        );
        const nonCompliantIndex = series.findIndex(
          (ser) => ser.name === "Non-Compliant"
        );
        alertAnalysis.forEach((dat) => {
          series[compliantIndex].data.push(dat.counts.compliant);
          series[nonCompliantIndex].data.push(dat.counts.nonCompliant);
        });

        let categories = [];
        categories = alertAnalysis.map((dat) => dat.standard.label.split(" "));
        chartstate.options = {
          ...chartstate.options,
          xaxis: { ...chartstate.options.xaxis, categories },
        };
        chartstate.series = series;
        chartstate.options.chart['events']= {
          dataPointSelection: (event, chartContext, config) => {
            let alert_types = [];
            if (category !== "compliance") {
              alert_types = alertAnalysis.map((dat) => dat.dimension.slug);
            } else {
              alert_types = alertAnalysis.map((dat) => dat.standard.slug);
            }
            const url = `/rs/category-threats-dashboard/category-threats?category=${category}&threat_type=${alert_types[config.dataPointIndex]}&alert_severity=${series[config.seriesIndex].name}&domain=${selectedCompanyContainer.domain}&date=${reportDateContainer}`;
            navigate(url);
          }
        }
        setCurrentAnalysisChatOptions(chartstate);
        dispatch(setLoading(false));
      }
    }
    else{
      let chartstate = {
        options: {
          colors: category === "compliance" ? ["#2fc430", "#eb4b2c"] : ["#2fc430", "#4baeec", "#4c4ccc", "#fc659c", "#eb4b2c"], 
          chart: {
            fontFamily: '"Montserrat", "Open Sans", sans-serif',
            type: "bar",
            height: 400,
            toolbar: {
              show: false
            }
          },
          plotOptions: {
            bar: {
              horizontal: false,
              columnWidth: "55%",
              endingShape: "rounded",
            },
          },
          dataLabels: {
            enabled: false,
          },
          stroke: {
            show: true,
            width: 2,
            colors: ["transparent"],
          },
          xaxis: {
            labels: {
              rotate: 0,
              hideOverlappingLabels: false,
              trim: false,
            },
          },
          yaxis: {
            title: {
              text: "Findings Count",
            },
            min:0,
            max:20,
          },
          fill: {
            opacity: 1,
          },
          tooltip: {
            y: {
              formatter: function (val) {
                if(val > 20){
                  return 20 + "+ Finding(s)";
                }
                else{
                  return val + " Finding(s)";
                }
              },
            },
          },
          legend: {
            position: "top",
          },
        },
        series: [
          {
            name: "Informational",
            data: [],
          },
          {
            name: "Low",
            data: [],
          },
          {
            name: "Medium",
            data: [],
          },
          {
            name: "High",
            data: [],
          },
          {
            name: "Critical",
            data: [],
          },
        ],
      };
      setCurrentAnalysisChatOptions(chartstate)
      dispatch(setLoading(false))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alertAnalysis]);
  
  // useEffect fetches alert analysis data based on category and sets the active date to report date incase if alert analysis data is present else sets the active date to current date
  useEffect(() => {
    const fetchCurrentAnalysisData = async() => {
      dispatch(setLoading(true));
      let params = {
        "domain":selectedCompanyContainer.domain,
        "date":reportDateContainer
      }
      if (category !== "over-all"){
        const dimension = dimensionsContainer.find(
          (dim) => dim.slug === category
        );
        params.dimension = dimension.id;
      }
      const response = await fetchAlertAnalysisService({ params });
      setAlertsAnalysis(response)
      dispatch(setLoading(false));
    };

    const fetchCompanyData = async() => {
      dispatch(setLoading(true));
      const companyId = selectedCompanyContainer.companyid;
      dispatch(fetchSingleCompanyService({ companyId })); 
      dispatch(setLoading(false));     
    };

    if (reportDateContainer && (dimensionsContainer.length>0)){
      fetchCompanyData()
      fetchCurrentAnalysisData();
      const formattedDate = new Date(reportDateContainer);
      setActiveDate(new Date(formattedDate));
    }
    else{
      setAlertsAnalysis([]);
      const formattedDate = new Date();
      setActiveDate(new Date(formattedDate));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportDateContainer, category, dimensionsContainer]);
  
   // Sets the filter based on company created date
  useEffect(() => {
    if (companyContainer){
      let filtersData = [];
      setActiveFilter(1);
      if (companyContainer.createdAt){
        const companyCreatedAt = new Date(companyContainer.createdAt);
        setCompanyCreatedDate(companyCreatedAt);
        const d = new Date();
        
        const months =
          d.getMonth() -
          companyCreatedAt.getMonth() +
          12 * (d.getFullYear() - companyCreatedAt.getFullYear()) +
          1;
        const diff_days = Math.round((d - companyCreatedAt)/(1000*60*60*24));
        
        let filtersIndex = allFiltersData.findIndex(
          (dat) => dat.value >= months
        );
        if (months > 12) {
          filtersIndex = allFiltersData.length - 1;
        }
        filtersData = allFiltersData.slice(0, filtersIndex + 1);

        let allFiltersOptions = [
        {
            label: "Last 1 Week",
            value: 7,
          },
          {
            label: "Last 2 Weeks",
            value: 14,
          },
          {
            label: "Last 3 Weeks",
            value: 21,
          },
          {
            label: "Last 6 Weeks",
            value: 42,
          }
          ];
        filtersIndex = allFiltersOptions.findIndex(
          (dat) => dat.value >= diff_days
        );
        if (diff_days > 43) {
          filtersIndex = allFiltersOptions.length - 1;
        }
        filtersData = [...allFiltersOptions.slice(0, filtersIndex+1), ...filtersData];
        setfiltersData(filtersData);
      }
      else{
        setfiltersData(allFiltersData);

      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyContainer]);
  
  // useEffect hook fetches all the report dates for a given company based on domain
  useEffect(() => {
    const fetchReportDatesData = async() => {
      let params = {
        "domain":selectedCompanyContainer.domain,
        "startDate":companyCreatedDate,
        "endDate": new Date(),
      }
      const response = await fetchReportDatesService({ params });
      if (response.dates){
        const formattedReportDates = response.dates.map(date => {
          return new Date(date).toISOString().split('T')[0];
        }); 
        if(response.dates)
        setReportDates(response.dates);        
        setHighlitedDays(formattedReportDates);
      }
    };

    if (companyCreatedDate){
      fetchReportDatesData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyCreatedDate]);

   // useEffect dispatches the set latest report date action when highlighted date changes
  useEffect(() => {
    if (date && reportDateContainer &&reportDates?.length>0) {
      const formattedDate = new Date(date).toISOString();
      const reportDateFormatted = new Date(reportDateContainer).toISOString();

      const formattedDateStr = formattedDate.split('T')[0];
      const reportDateStr = reportDateFormatted.split('T')[0];
      let dated='';
      if (formattedDateStr !== reportDateStr) {
        for (const report of reportDates) {
          const reportDatePart = report.split('T')[0];
          if (formattedDateStr === reportDatePart) {

            dated=reportDatePart;
            dispatch(setLatestReportDateSlice(report));
          }
        }
        if(dated==='')
        dispatch(setLatestReportDateSlice(formattedDate));
      }

    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);
  
  // Handles change in filter values
  const handleDropdownChange = (event) => {
    const selectedValue = parseInt(event.target.value);
    setActiveFilter(selectedValue);
  };
  


  return (
    <div>
      <Box sx={{ width: '100%', typography: 'body1' }}>
      <TabContext value={value}>
        <div className='d-flex justify-content-around'>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', width:'75%' }}>
            <TabList onChange={(e,val) => setValue(val)} className='overall-dashboard-tab'>
              <Tab label="Current Analysis" value="1" />
              <Tab label="Vulnerability Trend Analysis" value="2" />
            </TabList>
          </Box>

          {value === "2" ? (
            <Select
              variant="outlined"
              color="primary"
              value={activeFilter}
              onChange={handleDropdownChange}
              size="small"
            >
              {filters.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          ): (
              <LocalizationProvider dateAdapter={AdapterDayjs} >
                <DatePicker label="Choose the Date" 
                  className='overall-dashboard-calender'
                  value={dayjs(date)}
                  minDate={dayjs(companyCreatedDate)}
                  maxDate={dayjs(new Date())}
                  onChange={(newValue)=> setActiveDate(newValue)}
                  slots={{
                    day: ServerDay,
                  }}
                  slotProps={{
                    day: {
                      highlightedDays,
                    },
                  }}
                />
              </LocalizationProvider>
          )}
        </div>
               
        <TabPanel value="1">
          <Chart
            options={currentAnalysisChatOptions.options}
            series={currentAnalysisChatOptions.series}
            type="bar"
            height="250"
          />
        </TabPanel>
        <TabPanel value="2">
          <VulnerabilityTrendAnalysis activeFilter={activeFilter} reportDate={reportDateContainer} />
        </TabPanel>
      </TabContext>
    </Box>
    </div>
  )
}

export default CurrentAnalysis;
